import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";

import { AuthService } from "@azm/core/services/auth";
import { TOKEN_ACCESS_LOGOUT_RETURN_URL_KEY } from "@azm/core/constants/token-access-logout-return-url";
import { SSO_BUSINESS_LOGIN_LAST_ERROR_KEY, SSO_BUSINESS_LOGIN_PAYLOAD_KEY } from "@azm/core/constants/sso-business-login-storage";
import { isExternalAppType } from "@azm/core/enums/app-types";
import { TokenAccessErrorReason } from "@azm/core/enums/token-access-error-reason";
import { UserRoles } from "@azm/core/enums/user-roles";
import {
	coerceBusinessAccountsFromTokenAccess,
	soleEligibleActiveBusinessFirmForSso,
} from "@azm/core/utilities/business-sso-token-access";
import {
	readExternalAccessTokenQueryFromLocation,
	stripExternalAccessTokenParamsFromBrowserUrl,
} from "@azm/core/utilities/external-access-token-query";
import { pickTokenAccessReturnUrl } from "@azm/core/utilities/safe-internal-return-url";
import { BusinessService } from "@azm/features/auth/services/business";
import type { IBusinessAccountsResponse } from "@azm/features/auth/interfaces/business";
import {
	SSO_BUSINESS_ACCOUNTS_KEY,
	SSO_GOVERNMENT_ACCOUNTS_KEY,
	writeSsoScratch,
} from "@azm/features/auth/utilities/sso-scratch-storage";
import { environment } from "@azm/environments/environment";

export interface ExternalTokenAccessInitializerContext {
	authService: AuthService;
	businessService: BusinessService;
	router: Router;
}

const HOME_BY_ROLE: Partial<Record<UserRoles, string>> = {
	[UserRoles.Individual]: "/individual",
	[UserRoles.Business]: "/business",
	[UserRoles.Government]: "/government",
	[UserRoles.Internal]: "/dashboard",
};

function readTokenAccessErrorPayload(err: unknown): { message: string; errors?: string[] } {
	const http = err instanceof HttpErrorResponse ? err : null;
	return {
		message: (http?.error as { message?: string } | undefined)?.message ?? "",
		errors: (http?.error as { errors?: string[] } | undefined)?.errors,
	};
}

function clearTokenAccessSession(authService: AuthService): void {
	authService.clearSessionOnly();
	if (typeof sessionStorage !== "undefined") {
		sessionStorage.removeItem(TOKEN_ACCESS_LOGOUT_RETURN_URL_KEY);
	}
}

async function navigateToTokenAccessError(router: Router, reason: TokenAccessErrorReason): Promise<void> {
	await router.navigateByUrl(`/token-access-error?reason=${reason}`, { replaceUrl: true });
}

async function handleBusinessTokenAccess(
	authService: AuthService,
	businessService: BusinessService,
	router: Router,
	tokenAccessData: unknown,
): Promise<void> {
	const businessAccounts: IBusinessAccountsResponse | null =
		coerceBusinessAccountsFromTokenAccess(tokenAccessData) ??
		(typeof tokenAccessData === "object" &&
		tokenAccessData !== null &&
		typeof (tokenAccessData as IBusinessAccountsResponse).userId === "string"
			? (tokenAccessData as IBusinessAccountsResponse)
			: null);

	const soleEligible = businessAccounts ? soleEligibleActiveBusinessFirmForSso(businessAccounts) : null;

	if (businessAccounts?.userId && soleEligible?.firmId) {
		const retryPayload = { userId: businessAccounts.userId, beneficiaryFirmId: soleEligible.firmId };
		sessionStorage.setItem(SSO_BUSINESS_LOGIN_PAYLOAD_KEY, JSON.stringify(retryPayload));

		try {
			const loginRes = await firstValueFrom(businessService.login(retryPayload));
			sessionStorage.removeItem(SSO_BUSINESS_LOGIN_PAYLOAD_KEY);
			sessionStorage.removeItem(SSO_BUSINESS_LOGIN_LAST_ERROR_KEY);
			authService.verifyLoginSimulation(loginRes, tokenAccessData);
			return;
		} catch (err: unknown) {
			clearTokenAccessSession(authService);
			sessionStorage.setItem(SSO_BUSINESS_LOGIN_LAST_ERROR_KEY, JSON.stringify(readTokenAccessErrorPayload(err)));
			await navigateToTokenAccessError(router, TokenAccessErrorReason.BusinessLogin);
			return;
		}
	}

	writeSsoScratch(SSO_BUSINESS_ACCOUNTS_KEY, JSON.stringify(businessAccounts ?? tokenAccessData));
	await router.navigateByUrl("/auth/register/business", { replaceUrl: true });
}

export async function initializeExternalTokenAccess(context: ExternalTokenAccessInitializerContext): Promise<void> {
	const { authService, businessService, router } = context;

	if (!isExternalAppType(environment.defaultAppType)) return;

	const { accessToken, roleId: roleIdParam, identityId } = readExternalAccessTokenQueryFromLocation(window.location);

	if (!accessToken || !roleIdParam) return;

	const identityIdTrimmed = identityId.trim();
	if (!identityIdTrimmed) {
		await navigateToTokenAccessError(router, TokenAccessErrorReason.MissingIdentity);
		return;
	}

	const roleId = Number(roleIdParam) as UserRoles;
	if (
		!Number.isFinite(roleId) ||
		(roleId !== UserRoles.Individual && roleId !== UserRoles.Business && roleId !== UserRoles.Government)
	) {
		await navigateToTokenAccessError(router, TokenAccessErrorReason.Api);
		return;
	}

	clearTokenAccessSession(authService);

	let tokenAccessData: unknown;
	try {
		tokenAccessData = await firstValueFrom(authService.loginByAccessToken(accessToken, roleId, identityIdTrimmed));
	} catch (err: unknown) {
		clearTokenAccessSession(authService);
		sessionStorage.setItem("token-access-last-error", JSON.stringify(readTokenAccessErrorPayload(err)));
		await navigateToTokenAccessError(router, TokenAccessErrorReason.Api);
		return;
	}

	stripExternalAccessTokenParamsFromBrowserUrl();

	if (roleId === UserRoles.Business) {
		await handleBusinessTokenAccess(authService, businessService, router, tokenAccessData);
		return;
	}

	if (roleId === UserRoles.Government) {
		writeSsoScratch(SSO_GOVERNMENT_ACCOUNTS_KEY, JSON.stringify(tokenAccessData));
		await router.navigateByUrl("/auth/register/government", { replaceUrl: true });
		return;
	}

	if (!authService.isAuthenticated) {
		await navigateToTokenAccessError(router, TokenAccessErrorReason.Api);
		return;
	}

	const fallback = HOME_BY_ROLE[roleId] ?? "/individual";
	const logoutReturn = pickTokenAccessReturnUrl(tokenAccessData);
	if (typeof sessionStorage !== "undefined" && logoutReturn) {
		sessionStorage.setItem(TOKEN_ACCESS_LOGOUT_RETURN_URL_KEY, logoutReturn);
	}
	await router.navigateByUrl(fallback, { replaceUrl: true });
}
